import React from 'react'

interface ContentSectionProps {
  title?: string
  subtitle?: string
  description?: string
  items?: Array<{
    title: string
    description: string
    icon?: React.ReactNode
  }>
  image?: {
    src: string
    alt: string
    layout: 'full-width' | 'left-boundary' | 'right-boundary'
  }
  backgroundColor?: 'white' | 'gray' | 'gradient'
  textAlign?: 'left' | 'center'
  itemStyle?: 'default' | 'bento'
  className?: string
}

const ContentSection: React.FC<ContentSectionProps> = ({
  title,
  subtitle,
  description,
  items,
  image,
  backgroundColor = 'white',
  textAlign = 'left',
  itemStyle = 'default',
  className = ''
}) => {
  const getBgClasses = () => {
    switch (backgroundColor) {
      case 'gray':
        return 'bg-gray-50'
      case 'gradient':
        return 'bg-gradient-to-br from-gray-50 to-gray-100'
      default:
        return 'bg-white'
    }
  }

  const getTextAlignClasses = () => {
    return textAlign === 'center' ? 'text-center' : 'text-left'
  }

  const renderImage = () => {
    if (!image) return null

    const imageElement = (
      <img
        src={image.src}
        alt={image.alt}
        className="w-full h-auto rounded-lg shadow-lg"
      />
    )

    switch (image.layout) {
      case 'full-width':
        return (
          <div className="w-full mb-12">
            {imageElement}
          </div>
        )
      case 'left-boundary':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              {imageElement}
            </div>
            <div className="order-1 lg:order-2">
              {renderContent()}
            </div>
          </div>
        )
      case 'right-boundary':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {renderContent()}
            </div>
            <div>
              {imageElement}
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const renderContent = () => (
    <div className={getTextAlignClasses()}>
      {title && (
        <h2 className="text-3xl md:text-4xl font-bold text-dark-plum mb-6">
          {title}
        </h2>
      )}
      {subtitle && (
        <h3 className="text-xl md:text-2xl font-semibold text-dark-plum mb-4">
          {subtitle}
        </h3>
      )}
      {description && (
        <p className="text-lg text-gray-700 mb-8 leading-relaxed">
          {description}
        </p>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item, index) => (
            itemStyle === 'bento' ? (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300"
              >
                {item.icon && (
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-green to-secondary-green rounded-xl flex items-center justify-center text-white mb-4">
                    {item.icon}
                  </div>
                )}
                <h4 className="text-xl font-poppins font-semibold text-dark-plum mb-3">
                  {item.title}
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ) : (
              <div key={index} className="flex items-start space-x-4">
                {item.icon && (
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-green rounded-full flex items-center justify-center text-white">
                    {item.icon}
                  </div>
                )}
                <div className={item.icon ? '' : 'ml-0'}>
                  <h4 className="text-lg font-semibold text-dark-plum mb-2">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  )

  return (
    <section className={`section-padding ${getBgClasses()} ${className}`}>
      <div className="container-custom">
        {image ? (
          renderImage()
        ) : (
          <div className={`max-w-6xl ${textAlign === 'center' ? 'mx-auto' : ''}`}>
            {renderContent()}
          </div>
        )}
      </div>
    </section>
  )
}

export default ContentSection